import {
  FileIcon,
  FolderIcon,
  GlobeIcon,
  HardDriveIcon,
  SettingsIcon,
  ShieldIcon,
  Trash2Icon,
} from 'lucide-react';
import { type JSX, useEffect, useState } from 'react';

import { AppDialog } from '@/components/ui/app-dialog.js';
import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import type {
  SharePointPlace,
  SharePointPlaceAutomationAccess,
  SharePointPlaceInteractiveAccess,
  SharePointPlaceLevel,
} from '@/lib/api/sharepoint.js';

import { cn } from '@/lib/utils.js';

import type { PlaceIndexStanding } from './place-index.js';
import { meaningfulPath, pathFromWebUrl } from './row-notes.js';
import { reachName } from './selected-folders.js';
import {
  KNOWLEDGE_OFF_CONFIRM,
  KNOWLEDGE_OFF_WARNING,
} from '@/features/microsoft/knowledge-off.js';

/**
 * What each level is called, in Microsoft's own words. "Library" on its own is our abbreviation of
 * SharePoint's "document library" and reads like a place books live; the icon does not rescue it,
 * because the one a library wears is a HARD DRIVE, which says nothing to anybody.
 *
 * The word is kept, and only the pill it used to sit in is gone: whether a row is the whole site or
 * one folder inside it is the most consequential thing about that row, and a globe beside a folder
 * is a convention a reader has to already know.
 */
const LEVEL_LABELS: Record<SharePointPlaceLevel, string> = {
  site: 'Site',
  library: 'Document library',
  folder: 'Folder',
  file: 'File',
};

const LEVEL_ICONS: Record<SharePointPlaceLevel, typeof GlobeIcon> = {
  site: GlobeIcon,
  library: HardDriveIcon,
  folder: FolderIcon,
  file: FileIcon,
};

function interactiveAccessLabel(
  access: SharePointPlaceInteractiveAccess,
): string {
  return access === 'read_write' ? 'Read and write' : 'Read';
}

function automationAccessLabel(
  access: SharePointPlaceAutomationAccess,
): string {
  if (access === 'read_write') return 'Read and write';
  if (access === 'read') return 'Read';
  return 'Off';
}

/** Plain text explaining who may change the restriction, for a caller who is not the owner. */
function ownerOnlyText(enforced: boolean): string {
  return enforced
    ? 'This connection is restricted to the places below. Only the person who connected this SharePoint account can change them.'
    : 'This connection is not restricted — the agent can currently reach this whole SharePoint account. Only the person who connected it can restrict it.';
}

/**
 * The allowed places of one SharePoint connection, and what an owner may do to them: restrict a
 * connection that currently reaches everything, add a place, rename one or change either of its two
 * access levels, and remove one. Modelled on {@link OneDriveScopeList} for the same reason it exists —
 * alias-or-name, path, an unreachable mark, and the rename / change-access / remove actions — with one
 * addition this list needs and OneDrive's scopes do not: the connection itself starts unrestricted, so
 * there is a state before there is a list to show at all.
 *
 * `isOwner` is a plain prop, not something this component derives, because only the connection's owner
 * may mutate its places — a workspace role does not grant it — and that fact is not visible in anything
 * this component is handed (the places themselves carry no owner field on the wire). A caller that gets
 * this wrong shows a control nobody may use; there is no state in which a partial answer is safe, so
 * every mutating control — rename, remove, add a place, restrict — is left OUT of the tree entirely for
 * a non-owner, not merely disabled, exactly as {@link OneDriveScopeList}'s own `knowledge` prop is left
 * out rather than half-offered to a surface that should not have it.
 *
 * **Every one of the three states says what it is, the owner's included.** That was not true at first:
 * a non-owner got a sentence either way and an owner got one only while the connection was still
 * unrestricted, so the one person who can CHANGE the restriction was the only one never told it was in
 * force. It read as a bare list under a button. The gap was invisible while this lived on a tab called
 * "Allowed places" — the tab name carried the meaning — and surfaced the moment the list moved inside
 * {@link SharePointTab}, where nothing named it. A control's own screen has to explain the state it
 * controls; a heading somewhere else is not a substitute for one.
 */
/** Under the knowledge switch of a site or a library: one source's limit may not hold all of it. */
export const WHOLE_PLACE_KNOWLEDGE_LIMIT =
  'A whole site or library can hold more than one knowledge source indexes. To index all of it, add its folders instead.';

/** How a place's knowledge standing reads on its row. */
const INDEX_LABELS: Record<PlaceIndexStanding, string> = {
  on: 'Index: on',
  off: 'Index: off',
};

export function SharePointPlaceList({
  enforced,
  personalWorkspace = false,
  wholeOneDrive = false,
  places,
  isOwner,
  searchable,
  compact = false,
  busy = false,
  onRestrict,
  onAllowAll,
  onAddPlace,
  onUpdate,
  onDelete,
  indexStanding,
}: {
  /** Whether the connection's restriction is turned on at all. */
  enforced: boolean;
  /**
   * Whether this is the caller's own personal workspace. Unrestricted there is the intended state,
   * not a step still to take: nothing is offered to restrict, and a place's access levels decide
   * nothing, so only the knowledge base is left to choose. Null until the workspace's kind has
   * arrived, and then no way to change the mode is offered at all.
   */
  personalWorkspace?: boolean | null;
  /** Whether the pair holds the whole-drive OneDrive grant, so the open sentence names OneDrive. */
  wholeOneDrive?: boolean | null;
  places: readonly SharePointPlace[];
  /** Whether the caller may add, rename, re-level, remove a place, or flip the restriction. */
  isOwner: boolean;
  /**
   * Whether the contents are in the workspace's search index, and how to change it — the one setting
   * that used to live as a mark on the row. `of` answers null where the question does not apply.
   *
   * Left out entirely by a surface that has no business offering it, the same way `knowledge` is: one
   * prop, so a dialog never shows a control it cannot honour.
   */
  searchable?: {
    of: (row: SharePointPlace) => boolean | null;
    set: (row: SharePointPlace, next: boolean) => Promise<void>;
    /**
     * How the indexing is going, in the same words the Knowledge tab uses. Null where the row is
     * not on the shelf, or where the indexer has not answered — a switch that is on and says
     * nothing is the state somebody opened this to check.
     */
    statusOf?: (row: SharePointPlace) => string | null;
  };
  /**
   * Whether the row is standing in a narrow column, where a labelled button costs more than it
   * says. Measured: beside the browser at a laptop width the actions took 130px of a 285px row and
   * left the name 89px to wrap in, which tripled the row's height. The label goes; the
   * `aria-label` does not, so nothing is lost to a screen reader.
   */
  compact?: boolean;
  /** Disables every mutating control while a policy change from THIS list is in flight. */
  busy?: boolean;
  /**
   * Opens the restrict-confirmation flow. Only ever called for an owner viewing an unrestricted
   * connection; the flip to `enforced: true` itself happens there, not here — spec §13 requires
   * showing what enforcement affects before it is written, and this list has neither the knowledge
   * sources nor the per-place access editor that confirmation needs.
   */
  onRestrict: () => void;
  /**
   * Opens the pair again: everything reachable, the list back to the knowledge base only. Offered
   * only in the owner's own personal workspace, the one place a pair may be open.
   */
  onAllowAll?: () => void;
  /**
   * Starts adding a place — what happens next belongs to whatever mounts this list. Left out by a
   * surface that shows the whole SharePoint underneath the list, where adding is a button on the row
   * itself and a second way in would be one way too many.
   */
  onAddPlace?: () => void;
  onUpdate: (
    placeId: string,
    patch: {
      alias: string | null;
      interactiveAccess: SharePointPlaceInteractiveAccess;
      automationAccess: SharePointPlaceAutomationAccess;
    },
  ) => Promise<unknown>;
  onDelete: (placeId: string) => Promise<unknown>;
  /**
   * Where each place stands on the workspace's knowledge shelf. Left out by a surface that has not
   * read the shelf, and the rows then say nothing about it rather than guessing "off".
   */
  indexStanding?: (place: SharePointPlace) => PlaceIndexStanding;
}): JSX.Element {
  /** Unrestricted because it is the owner's own personal workspace, which is how it is meant to be. */
  const openByDesign = personalWorkspace === true && !enforced;
  const [editing, setEditing] = useState<SharePointPlace | null>(null);
  const [removing, setRemoving] = useState<SharePointPlace | null>(null);
  const [alias, setAlias] = useState('');
  const [interactiveAccess, setInteractiveAccess] =
    useState<SharePointPlaceInteractiveAccess>('read');
  const [automationAccess, setAutomationAccess] =
    useState<SharePointPlaceAutomationAccess>('none');
  const [error, setError] = useState<string | null>(null);
  const [rowBusy, setRowBusy] = useState(false);
  /** Null while the question does not apply to the open row, which is also when no switch shows. */
  const [indexed, setIndexed] = useState<boolean | null>(null);
  /** Set once Save was pressed with the index switched off, so the next press is the one that removes. */
  const [confirmingOff, setConfirmingOff] = useState(false);
  /**
   * Read live rather than kept in state: the pass moves while this dialog is open, and a copy taken
   * when it opened would still say "Indexing…" long after it finished.
   */
  const indexStatus =
    editing && indexed === true
      ? (searchable?.statusOf?.(editing) ?? null)
      : null;

  useEffect(() => {
    if (!editing) return;
    setAlias(editing.alias ?? '');
    setInteractiveAccess(editing.interactiveAccess);
    setAutomationAccess(editing.automationAccess);
    setIndexed(searchable ? searchable.of(editing) : null);
    setConfirmingOff(false);
    setError(null);
  }, [editing]);

  async function save(): Promise<void> {
    if (!editing) return;
    const turningOff =
      searchable !== undefined && indexed === false && searchable.of(editing);
    if (turningOff && !confirmingOff) {
      setConfirmingOff(true);
      return;
    }
    setRowBusy(true);
    setError(null);
    try {
      await onUpdate(editing.id, {
        alias: alias.trim() || null,
        interactiveAccess,
        automationAccess,
      });
      /**
       * Indexing is a separate registry, so it is a separate call — and it runs only when the
       * switch actually moved, so a save that changed a name does not re-register anything.
       */
      if (
        searchable &&
        indexed !== null &&
        indexed !== searchable.of(editing)
      ) {
        await searchable.set(editing, indexed);
      }
      setEditing(null);
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : 'Could not save this place.',
      );
    } finally {
      setRowBusy(false);
    }
  }

  async function remove(): Promise<void> {
    if (!removing) return;
    setRowBusy(true);
    setError(null);
    try {
      await onDelete(removing.id);
      setRemoving(null);
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : 'Could not remove this place.',
      );
    } finally {
      setRowBusy(false);
    }
  }

  return (
    /*
      Compact is a COLUMN that fills its share of the dialog: the list box stretches and scrolls
      inside itself, so the panel beside it ends at the same line. Sized to its content, it stopped
      halfway down and left the two sides visibly out of step.
    */
    <div
      className={cn(
        'flex flex-col gap-3',
        compact && 'min-h-0 flex-1 overflow-hidden',
      )}
    >
      {!isOwner && (
        <p className="text-sm text-muted-foreground">
          {ownerOnlyText(enforced)}
        </p>
      )}

      {isOwner && openByDesign && compact && (
        <p className="text-sm text-muted-foreground">
          Add an item here only to put it in the knowledge base.
        </p>
      )}

      {isOwner && openByDesign && !compact && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-muted-foreground">
            In your personal workspace the agent reaches all of{' '}
            {reachName(wholeOneDrive)}. Add an item here only to put it in the
            knowledge base.
          </p>
          <Button size="sm" disabled={busy} onClick={onRestrict}>
            <ShieldIcon /> Restrict access
          </Button>
        </div>
      )}

      {isOwner && !enforced && personalWorkspace === false && (
        <div
          role="alert"
          className="flex flex-col gap-2 border-l-2 border-muted-foreground/40 py-2 pl-4"
        >
          <p className="text-sm">
            The agent reaches all of this SharePoint. Add an item here to put it
            in the knowledge base. To set access item by item, add the items and
            press Restrict access: the agent then reaches only them.
          </p>
          <div>
            <Button size="sm" disabled={busy} onClick={onRestrict}>
              <ShieldIcon /> Restrict access
            </Button>
          </div>
        </div>
      )}

      {isOwner && enforced && !compact && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-muted-foreground">
            The agent reaches only the places below in this SharePoint.
          </p>
          <span className="flex gap-2">
            {onAddPlace && (
              <Button size="sm" disabled={busy} onClick={onAddPlace}>
                <FolderIcon /> Add a place
              </Button>
            )}
            {personalWorkspace === true && onAllowAll && (
              <Button
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={onAllowAll}
              >
                Allow all
              </Button>
            )}
          </span>
        </div>
      )}

      {places.length === 0 ? (
        <div
          className={cn(
            'rounded-xl border border-border p-2',
            compact && 'flex min-h-0 flex-1 items-center justify-center',
          )}
        >
          <p className="px-3 py-2 text-center text-sm text-muted-foreground">
            {!isOwner
              ? 'Nothing has been added yet.'
              : compact
                ? 'Nothing yet. What you add on the right appears here.'
                : 'No places have been added yet.'}
          </p>
        </div>
      ) : (
        <div
          role="list"
          className={cn(
            'flex flex-col gap-1 rounded-xl border border-border p-2',
            compact && 'min-h-0 flex-1 overflow-y-auto',
          )}
        >
          {places.map((place) => {
            const Icon = LEVEL_ICONS[place.level];
            const name = place.alias ?? place.nameSnapshot;
            /**
             * A place added before the path was stored has none, so its URL answers instead — two
             * libraries both called `Documents` are otherwise the same row twice.
             */
            const path =
              meaningfulPath(place.parentPathSnapshot) ??
              pathFromWebUrl(place.webUrlSnapshot);
            return (
              <div
                key={place.id}
                role="listitem"
                className="flex items-center gap-2 rounded-md px-2 py-2 text-sm hover:bg-muted"
              >
                <Icon
                  className="mt-0.5 size-4 shrink-0 text-muted-foreground"
                  aria-hidden
                />
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-start justify-between gap-2">
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium">
                        {name}
                      </span>
                      {/*
                        What it is and where it sits, on one muted line. Two facts that belong
                        together, and neither is worth competing with the name for attention.
                      */}
                      <span className="block text-xs text-muted-foreground">
                        {path === null
                          ? LEVEL_LABELS[place.level]
                          : `${LEVEL_LABELS[place.level]} · ${path}`}
                      </span>
                      {/*
                        While everything is reachable, the knowledge base is the only reason a row
                        is here; one that is not in it would otherwise look like it does nothing.
                      */}
                      {openByDesign && searchable?.of(place) === false && (
                        <span className="block text-xs text-muted-foreground">
                          Not in knowledge
                        </span>
                      )}
                    </span>
                    <span className="flex shrink-0 gap-1">
                      {!place.reachable && (
                        <Badge variant="destructive">Unreachable</Badge>
                      )}
                    </span>
                  </span>
                  {/*
                    A compact row says WHAT it is and nothing about how it is set. Everything an add
                    grants is the same on every row, so the settings read as noise; a place somebody
                    narrowed states it where it is changed, behind the gear.
                  */}
                  {!compact && (
                    <span className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                      {!openByDesign && (
                        <>
                          <span>
                            Interactive:{' '}
                            {interactiveAccessLabel(place.interactiveAccess)}
                          </span>
                          <span>
                            Automation:{' '}
                            {automationAccessLabel(place.automationAccess)}
                          </span>
                        </>
                      )}
                      {indexStanding && (
                        <span>{INDEX_LABELS[indexStanding(place)]}</span>
                      )}
                    </span>
                  )}
                </span>
                {isOwner && (
                  <div className="flex shrink-0 items-center gap-0.5">
                    <Button
                      variant="ghost"
                      size={compact ? 'icon-sm' : 'sm'}
                      aria-label="Place settings"
                      disabled={busy}
                      onClick={() => setEditing(place)}
                    >
                      <SettingsIcon />
                      {!compact && 'Settings'}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Remove place"
                      disabled={busy}
                      onClick={() => {
                        setError(null);
                        setRemoving(place);
                      }}
                    >
                      <Trash2Icon />
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {error && isOwner && editing === null && removing === null && (
        <p className="text-sm text-destructive">{error}</p>
      )}

      {isOwner && (
        <AppDialog
          open={editing !== null}
          onOpenChange={(open) => {
            if (!open) setEditing(null);
          }}
          title="Place settings"
          description={
            !openByDesign
              ? 'Change the display name and the two independent access levels.'
              : searchable
                ? 'Change the display name and whether it is in the knowledge base.'
                : 'Change the display name.'
          }
        >
          <div className="flex flex-col gap-4 pt-3">
            <label className="flex flex-col gap-1 text-sm">
              Place alias
              <Input
                value={alias}
                onChange={(event) => setAlias(event.target.value)}
              />
            </label>
            {!openByDesign && (
              <>
                <label className="flex flex-col gap-1 text-sm">
                  Interactive access
                  <select
                    className="h-10 rounded-md border border-input bg-background px-3"
                    value={interactiveAccess}
                    onChange={(event) =>
                      setInteractiveAccess(
                        event.target.value as SharePointPlaceInteractiveAccess,
                      )
                    }
                  >
                    <option value="read">Read</option>
                    <option value="read_write">Read and write</option>
                  </select>
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  Automation access
                  <select
                    className="h-10 rounded-md border border-input bg-background px-3"
                    value={automationAccess}
                    onChange={(event) =>
                      setAutomationAccess(
                        event.target.value as SharePointPlaceAutomationAccess,
                      )
                    }
                  >
                    <option value="none">Off</option>
                    <option value="read">Read</option>
                    <option value="read_write">Read and write</option>
                  </select>
                </label>
              </>
            )}
            {indexed !== null && (
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={indexed}
                  onChange={(event) => {
                    setIndexed(event.target.checked);
                    setConfirmingOff(false);
                  }}
                />
                <span className="flex flex-col gap-0.5">
                  In the workspace knowledge base
                  {/*
                    One line, and the state beside it.

                    The old copy explained the whole mechanism — what is indexed, where it is listed,
                    what survives turning it off, what happens on the way back. Somebody opening this
                    wants two things: what the switch buys, and whether it has happened yet.
                  */}
                  <span className="text-xs text-muted-foreground">
                    The assistant can quote it without being pointed at
                    SharePoint.
                  </span>
                  {editing &&
                    (editing.level === 'site' ||
                      editing.level === 'library') && (
                      <span className="text-xs text-muted-foreground">
                        {WHOLE_PLACE_KNOWLEDGE_LIMIT}
                      </span>
                    )}
                  {indexStatus && (
                    <span className="text-xs text-muted-foreground">
                      {indexStatus}
                    </span>
                  )}
                </span>
              </label>
            )}
            {error && <p className="text-sm text-destructive">{error}</p>}
            {confirmingOff && (
              <p className="text-sm text-destructive">
                {KNOWLEDGE_OFF_WARNING}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setEditing(null)}>
                Cancel
              </Button>
              <Button disabled={rowBusy} onClick={() => void save()}>
                {confirmingOff ? KNOWLEDGE_OFF_CONFIRM : 'Save settings'}
              </Button>
            </div>
          </div>
        </AppDialog>
      )}

      {isOwner && (
        <AppDialog
          open={removing !== null}
          onOpenChange={(open) => {
            if (!open) setRemoving(null);
          }}
          title="Remove place"
          description={
            openByDesign
              ? "It leaves this list and this workspace's knowledge base. The agent still reaches it, since it reaches all of SharePoint here. Nothing in SharePoint is deleted."
              : "The agent and every browse surface lose this place immediately, and it leaves this workspace's knowledge base. Nothing in SharePoint is deleted."
          }
        >
          <div className="flex flex-col gap-4 pt-3">
            {error && <p className="text-sm text-destructive">{error}</p>}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setRemoving(null)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                disabled={rowBusy}
                onClick={() => void remove()}
              >
                Remove place
              </Button>
            </div>
          </div>
        </AppDialog>
      )}
    </div>
  );
}
